import axios from "axios";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useAxiosHook } from "./useAxiosHook";

const ROUTE_PREFIX = "loan_terms";
const LOAN_TERM_OBJECTS = {
  ORIGINATOR_FEES: "originatorFees",
  ORIGINATOR_CREDITS: "originatorCredits",
  ESCROW_ITEMS: "escrowItems",
  CORRESPONDENT_FEES: "correspondentFees",
  BROKER_FEES: "brokerFees",
  THIRD_PARTY_FEES: "thirdPartyFees",
};
export const useLoanTermsHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;

  // -------------------------------------
  //  ###############TEMPORARY###############
  //  hideFailMsg: true,
  // -------------------------------------
  const getLoanTerms = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/get_loan_terms/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Loan Terms",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      hideFailMsg: true,
      onFailFn,
    });
  };

  const syncLoanWithSalesforceById = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/salesforce_sync/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Sync With Salesforce By ID",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Loan Synced Successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const getFundingEntities = async ({
    loanId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/get_funding_entities`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Funding Entities",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanTermsListObject = async ({
    dealId,
    objectName,
    data,
    onSuccessFn,
    onSuccessMsg,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectName, data });
    const endpoint = `${ROUTE_PREFIX}/create_list_object/${objectName}/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Create Loan Terms Object: ${objectName}`,
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLoanTermsListObject = async ({
    dealId,
    objectName,
    data,
    itemId,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectName, data, itemId });
    const endpoint = `${ROUTE_PREFIX}/update_list_object/${objectName}/${dealId}/${itemId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Loan Terms Object",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const deleteLoanTermsListObject = async ({
    dealId,
    objectName,
    itemId,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectName, itemId });
    const endpoint = `${ROUTE_PREFIX}/delete_list_object/${objectName}/${dealId}/${itemId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Loan Terms Object",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateFundingEntity = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, data });
    const endpoint = `${ROUTE_PREFIX}/set_funding_entity/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Set Funding Entity`,
      path: endpoint,
      payload: data,
      onSuccessMsg: "Funding Entity Updated Successfully",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateTitleContact = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/set_title_contact/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Set Title Contact`,
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Title Contact Updated Successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  /**
   * ESCROW
   */

  const getEscrowItems = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/get_escrow_items/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Escrow Items",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const setEscrowItem = async ({
    dealId,
    subjectPropertyId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, data, subjectPropertyId });
    const endpoint = `${ROUTE_PREFIX}/set_escrow_item/${dealId}/${subjectPropertyId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Set Escrow Item`,
      path: endpoint,
      payload: data,
      onSuccessMsg: "Escrow Item Set Successfully",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateEscrowItem = async ({
    dealId,
    escrowItemId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, data, escrowItemId });
    const endpoint = `${ROUTE_PREFIX}/update_escrow_item/${dealId}/${escrowItemId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Update Escrow Item`,
      path: endpoint,
      payload: data,
      onSuccessMsg: "Escrow Item Updated Successfully",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const deleteEscrowItem = async ({
    dealId,
    escrowItemId,
    // data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, escrowItemId });
    const endpoint = `${ROUTE_PREFIX}/delete_escrow_item/${dealId}/${escrowItemId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Delete Escrow Item`,
      path: endpoint,
      payload: {},
      onSuccessMsg: "Escrow Item Deleted Successfully",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const sendFundingShieldRequest = async ({
    dealId,
    toRecipients,
    attachmentIds,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, toRecipients, attachmentIds });
    const endpoint = `${ROUTE_PREFIX}/send_funding_shield_request/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Sending Funding Shield Request`,
      path: endpoint,
      payload: {
        toRecipients,
        attachmentIds,
      },
      onSuccessMsg: "Funding Shield Request Sent Successfully",
      onFailMsg: "Failed to Send Funding Shield Request",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanTermsObject = async ({
    dealId,
    objectType, // wireInstructions || otherFutureUpdateableObject
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Loan Updated Successfully",
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectType, data });
    const endpoint = `${ROUTE_PREFIX}/create_object/${objectType}/${dealId}`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan Terms Object",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLoanTermsObject = async ({
    dealId,
    objectType, // wireInstructions || otherFutureUpdateableObject
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Loan Updated Successfully",
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectType, data });
    const endpoint = `${ROUTE_PREFIX}/update_object/${objectType}/${dealId}`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Loan Terms Object Data",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const getEmailTemplateNames = async ({
    onSuccessFn,

    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/email_template_names`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Email Template Names",
      path: endpoint,

      cancelSource: source,
      onSuccessFn,

      onFailFn,
    });
  };

  const previewEmailTemplate = async ({
    dealId,
    templateName, // wireInstructions || otherFutureUpdateableObject
    // data,
    onSuccessFn,
    onCompleteFn,

    onFailFn,
  }) => {
    validateRequiredFields({ dealId, templateName });
    const endpoint = `${ROUTE_PREFIX}/preview_email_template/${dealId}/${templateName}`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Preview Email Template",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,

      onCompleteFn,
      onFailFn,
    });
  };

  const sendEmailTemplate = async ({
    dealId,
    templateName, // wireInstructions || otherFutureUpdateableObject
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Email Template Sent Successfully",
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, templateName, data });
    const endpoint = `${ROUTE_PREFIX}/send_email_template/${dealId}/${templateName}`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Send Email Template",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  return {
    error,
    loading,
    getLoanTerms,
    syncLoanWithSalesforceById,
    getFundingEntities,
    createLoanTermsListObject,
    updateLoanTermsListObject,
    updateTitleContact,
    updateFundingEntity,
    getEscrowItems,
    updateEscrowItem,
    setEscrowItem,
    deleteEscrowItem,
    deleteLoanTermsListObject,
    sendFundingShieldRequest,
    createLoanTermsObject,
    updateLoanTermsObject,
    getEmailTemplateNames,
    sendEmailTemplate,
    previewEmailTemplate,
  };
};
